import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, statusBadge, priorityBadge, categoryColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Mail, Sparkles, Loader2, Copy, Inbox as InboxIcon, ArrowRight, DollarSign } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const WEBHOOK = `${process.env.REACT_APP_BACKEND_URL}/api/inbound/email`;

export default function Inbox() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ from_email: "", from_name: "", subject: "", body: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/inbound/emails").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const capture = async () => {
    if (!form.subject.trim() && !form.body.trim()) return toast.error(t("body"));
    setLoading(true);
    try {
      const res = await api.post("/inbound/email/simulate", form);
      toast.success(`${res.number} · ${res.title}`);
      setForm({ from_email: "", from_name: "", subject: "", body: "" });
      load();
    } catch {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(WEBHOOK);
    toast.success(t("copied"));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("inbox")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("emailCapture")}</p>
      </div>

      {/* Webhook config */}
      <div className="bg-slate-900 text-slate-200 rounded-xl p-5">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
          <Mail className="w-4 h-4" /> {t("webhookUrl")}
        </div>
        <div className="flex items-center gap-2">
          <code data-testid="webhook-url" className="flex-1 font-mono text-xs sm:text-sm bg-slate-800 rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap">{WEBHOOK}</code>
          <button data-testid="copy-webhook" onClick={copyWebhook} className="shrink-0 inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-2 text-sm transition-colors">
            <Copy className="w-4 h-4" /> {t("copied").replace("Copied", "Copy").replace("Copiado", "Copiar")}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">{t("webhookHint")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Simulate */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-5 h-fit">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">{t("simulate")}</h2>
          <p className="text-xs text-slate-500 mb-4">{t("aiIntakeHint")}</p>
          <div className="space-y-3">
            <input data-testid="sim-from" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} placeholder={t("fromEmail")} className={inputCls} />
            <input data-testid="sim-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={t("subject")} className={inputCls} />
            <textarea data-testid="sim-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={t("body")} className={inputCls} />
            <button data-testid="sim-capture" onClick={capture} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2.5 hover:bg-blue-700 transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? t("analyzing") : t("captureNow")}
            </button>
          </div>
        </div>

        {/* Captured list */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">{t("capturedTickets")}</h2>
          </div>
          <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-10 text-center text-slate-400">
                <InboxIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                {t("noData")}
              </div>
            )}
            {items.map((it) => (
              <div key={it.id} data-testid={`inbox-item-${it.id}`} className="px-5 py-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400">{it.number}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${categoryColor(it.category)}`}>{t(it.category)}</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${priorityBadge(it.priority)}`}>{t(it.priority)}</span>
                    </div>
                    <div className="font-medium text-slate-900 mt-1 truncate">{it.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {it.email_from || "—"}
                      {it.unit_name && <><ArrowRight className="w-3 h-3" /> {it.unit_name}</>}
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{it.description}</p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(it.status)}`}>{t(it.status === "completed" ? "completed_s" : it.status)}</span>
                    {it.estimate_status ? (
                      <button data-testid={`inbox-estimate-${it.id}`} onClick={() => navigate(`/estimates?ticket=${it.id}`)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(it.estimate_status)}`}>
                        <DollarSign className="w-3 h-3" />{t(it.estimate_status)}
                      </button>
                    ) : (
                      <button data-testid={`inbox-create-estimate-${it.id}`} onClick={() => navigate(`/estimates?ticket=${it.id}`)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                        <DollarSign className="w-3 h-3" />{t("createEstimate")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
